import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Zap } from 'lucide-react';

const SEOFooter = ({ language = 'en' }) => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  
  // Scroll to top when navigating to a page with SEOFooter
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  const footerLinks = {
    sports: {
      en: {
        title: 'Sports',
        links: [
          { name: 'NFL Predictions', path: '/nfl' },
          { name: 'MLB Analysis', path: '/mlb' },
          { name: 'Soccer/Football', path: '/football' },
          { name: 'MMA/UFC', path: '/mma' },
          { name: 'NCAA Sports', path: '/ncaa' },
          { name: 'Volleyball', path: '/volleyball' },
        ]
      },
      'pt-BR': {
        title: 'Esportes',
        links: [
          { name: 'Brasileirão Série A e B', path: '/pt-br/brasileirao' },
          { name: 'Copa Libertadores', path: '/pt-br/libertadores' },
          { name: 'Copa do Brasil', path: '/pt-br/copa-do-brasil' },
          { name: 'Campeonatos Estaduais', path: '/pt-br/estaduais' },
          { name: 'Análise de Cantos', path: '/pt-br/cantos-escanteios' },
          { name: 'Fazer a Fezinha', path: '/pt-br/fazer-fezinha' },
          { name: 'NFL', path: '/nfl' },
          { name: 'MLB', path: '/mlb' },
        ]
      }
    },
    product: {
      en: {
        title: 'Product',
        links: [
          { name: 'AI Chat', path: '/chat' },
          { name: 'Sports Hub', path: '/sports-hub' },
          { name: 'Pricing', path: '/pricing' },
          { name: 'FAQ', path: '/faq' },
          { name: 'Help & Support', path: '/help' },
        ]
      },
      'pt-BR': {
        title: 'Produto',
        links: [
          { name: 'Chat com IA', path: '/chat' },
          { name: 'Central de Esportes', path: '/sports-hub' },
          { name: 'Preços', path: '/pricing' },
          { name: 'Perguntas Frequentes', path: '/faq' },
          { name: 'Ajuda e Suporte', path: '/help' },
        ]
      }
    },
    company: {
      en: {
        title: 'Company',
        links: [
          { name: 'About Us', path: '/about-us' },
          { name: 'Terms & Conditions', path: '/terms-and-conditions' },
          { name: 'Privacy Policy', path: '/privacy' },
        ]
      },
      'pt-BR': {
        title: 'Empresa',
        links: [
          { name: 'Sobre Nós', path: '/about-us' },
          { name: 'Termos e Condições', path: '/terms-and-conditions' },
          { name: 'Política de Privacidade', path: '/privacy' },
        ]
      }
    },
    resources: {
      en: {
        title: 'Resources',
        links: [
          { name: 'AI Sports Betting Picks', path: '/ai-sports-betting-picks' },
          { name: 'AI vs Human Picks', path: '/ai-vs-human-picks' },
          { name: 'How to Use PickStar', path: '/resources/pickstar-onboarding' },
          { name: 'AI Exchange (AIX)', path: '/resources/aix' },
          { name: 'Sports Betting Guide', path: '/guide' },
          { name: 'AI Technology', path: '/technology' },
          { name: 'Responsible Gaming', path: '/responsible-gaming' },
        ]
      },
      'pt-BR': {
        title: 'Recursos',
        links: [
          { name: 'Palpites da IA', path: '/palpites-ia' },
          { name: 'Como Usar IA para Apostas', path: '/como-usar-ia-apostas-esportivas' },
          { name: 'Libertadores 2025', path: '/libertadores-ai-picks-2025' },
          { name: 'Como Usar o PickStar', path: '/resources/pickstar-onboarding' },
          { name: 'Câmbio de IA (AIX)', path: '/resources/aix' },
          { name: 'Guia de Apostas', path: '/pt-br/guia' },
          { name: 'Tecnologia IA', path: '/pt-br/tecnologia' },
          { name: 'Jogo Responsável', path: '/pt-br/jogo-responsavel' },
        ]
      }
    }
  };

  const lang = language === 'pt-BR' ? 'pt-BR' : 'en';
  
  return (
    <footer className="relative z-20 bg-gradient-to-b from-gray-900/50 to-black border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-20">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {/* Sports Column */}
          <div>
            <h3 className="text-white font-semibold mb-4">
              {footerLinks.sports[lang].title}
            </h3>
            <ul className="space-y-2">
              {footerLinks.sports[lang].links.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Product Column */}
          <div>
            <h3 className="text-white font-semibold mb-4">
              {footerLinks.product[lang].title}
            </h3>
            <ul className="space-y-2">
              {footerLinks.product[lang].links.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="text-white font-semibold mb-4">
              {footerLinks.company[lang].title}
            </h3>
            <ul className="space-y-2">
              {footerLinks.company[lang].links.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h3 className="text-white font-semibold mb-4">
              {footerLinks.resources[lang].title}
            </h3>
            <ul className="space-y-2">
              {footerLinks.resources[lang].links.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-400 hover:text-emerald-400 transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Left: Logo and Copyright */}
            <div className="flex flex-col md:flex-row items-center gap-3 order-2 md:order-1">
              <div className="flex items-center gap-2">
                <Zap className="w-6 h-6 text-emerald-400" />
                <span className="text-white font-bold text-lg">PickStar AI</span>
              </div>
              <span className="text-gray-400 text-sm hidden md:block">
                © {currentYear} PickStar Labs LLC
              </span>
            </div>

            {/* Right: Social Icons and Language Switcher */}
            <div className="flex items-center gap-6 order-1 md:order-2">
              {/* Social Links */}
              <div className="flex items-center gap-4">
                <a
                  href="https://twitter.com/PickStarAI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Twitter"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/@pickstarai-yt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="YouTube"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/pickstarai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a
                  href="https://www.reddit.com/r/PickStar/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label="Reddit"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
                  </svg>
                </a>
              </div>

              {/* Language Switcher */}
              <div className="flex items-center gap-3 border-l border-white/10 pl-6">
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <Link
                  to="/"
                  className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                >
                  English
                </Link>
                <span className="text-gray-600">|</span>
                <Link
                  to="/pt-br"
                  className="text-gray-400 hover:text-white transition-colors text-sm font-medium"
                >
                  Português
                </Link>
              </div>
            </div>
          </div>

          {/* Mobile Copyright and Disclaimer */}
          <div className="mt-6 text-center md:hidden">
            <p className="text-gray-400 text-sm mb-2">
              © {currentYear} PickStar Labs LLC
            </p>
            <p className="text-xs text-gray-500">
              {lang === 'pt-BR'
                ? 'Análise esportiva com IA é para pesquisa séria apenas. Aposte com responsabilidade.'
                : 'AI sports analysis is for serious research only. Please gamble responsibly.'
              }
            </p>
          </div>

          {/* Desktop Disclaimer */}
          <p className="text-xs text-gray-500 mt-6 text-center hidden md:block">
            {lang === 'pt-BR'
              ? 'Análise esportiva com IA é para pesquisa séria apenas. Aposte com responsabilidade.'
              : 'AI sports analysis is for serious research only. Please gamble responsibly.'
            }
          </p>
        </div>
      </div>
    </footer>
  );
};

export default SEOFooter;